/*───────────────────────────────────────────────────────────────
 📄 File: components/webrtc/WebRTCPanelHeader.tsx
 🧩 Description:
    Header bar and status row for the WebRTC side panel.
    Shows connection status, auto-answer toggle and extension.
    Extracted from WebRTCPhonePanel.tsx.
────────────────────────────────────────────────────────────────*/

import { Phone, Wifi, WifiOff, PhoneForwarded, ChevronRight } from 'lucide-react';
import { SimpleTooltip } from '../ui/SimpleTooltip';
import { PhoneStatus } from '../../hooks/useSipPhone';

interface Props {
    status: PhoneStatus;
    extension: string;
    autoAnswer: boolean;
    onToggle: () => void;
    onToggleAutoAnswer: () => void;
}

const statusColor = (s: PhoneStatus) => {
    switch (s) {
        case 'registered': return 'text-green-400';
        case 'connecting': return 'text-yellow-400';
        case 'error':      return 'text-red-400';
        default:           return 'text-muted-foreground';
    }
};

const statusText = (s: PhoneStatus) => {
    switch (s) {
        case 'registered': return 'Registered';
        case 'connecting': return 'Connecting...';
        case 'error':      return 'Error';
        default:           return 'Offline';
    }
};

export function WebRTCPanelHeader({ status, extension, autoAnswer, onToggle, onToggleAutoAnswer }: Props) {
    return (
        <>
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-border bg-muted/50">
                <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <span className="font-medium text-sm">WebRTC Phone</span>
                </div>
                <button onClick={onToggle} className="p-1 hover:bg-muted rounded transition-colors">
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>

            {/* Status Bar */}
            <div className="flex items-center justify-between mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                    {status === 'registered'
                        ? <Wifi className="w-3.5 h-3.5 text-green-400" />
                        : <WifiOff className="w-3.5 h-3.5 text-muted-foreground" />}
                    <span className={statusColor(status)}>{statusText(status)}</span>
                </div>
                <div className="flex items-center gap-2">
                    <SimpleTooltip content={autoAnswer ? 'Auto-Answer: ON' : 'Auto-Answer: OFF'} position="bottom">
                        <button
                            onClick={onToggleAutoAnswer}
                            className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${
                                autoAnswer
                                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                            }`}
                        >
                            <PhoneForwarded className="w-3 h-3" />
                            <span className="text-[10px]">Auto</span>
                        </button>
                    </SimpleTooltip>
                    <span className="text-muted-foreground">Ext. {extension}</span>
                </div>
            </div>
        </>
    );
}
